import {
  BrowserProvider,
  type Eip1193Provider,
  type Signer,
  getAddress,
} from "ethers";
import type { WalletState } from "../types";
import { SurfError, SurfErrorCode } from "../utils/errors";

type ListenerMap = {
  accountsChanged: Array<(accounts: string[]) => void>;
  chainChanged: Array<(chainId: number) => void>;
  disconnect: Array<() => void>;
};

export abstract class BaseWalletAdapter {
  protected provider: Eip1193Provider | undefined;
  private readonly listeners: ListenerMap = {
    accountsChanged: [],
    chainChanged: [],
    disconnect: [],
  };

  abstract readonly name: string;
  abstract get installed(): boolean;

  protected abstract resolveProvider(): Eip1193Provider | undefined;

  async connect(chainId: number): Promise<WalletState> {
    const provider = this.requireProvider();
    await this.switchChain(chainId);
    const accounts = (await provider.request({
      method: "eth_requestAccounts",
    })) as string[];
    if (!accounts[0]) {
      throw new SurfError(
        SurfErrorCode.WALLET_NOT_CONNECTED,
        `${this.name} did not return any connected account`,
      );
    }
    const chainHex = (await provider.request({
      method: "eth_chainId",
    })) as string;

    this.bindListeners(provider);

    return {
      address: getAddress(accounts[0]) as `0x${string}`,
      chainId: Number.parseInt(chainHex, 16),
      connected: true,
    };
  }

  async disconnect(): Promise<void> {
    this.removeAllListeners();
    this.listeners.accountsChanged = [];
    this.listeners.chainChanged = [];
    this.listeners.disconnect = [];
    this.provider = undefined;
  }

  async switchChain(chainId: number): Promise<void> {
    const provider = this.requireProvider();
    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${chainId.toString(16)}` }],
      });
    } catch (error: any) {
      if (error?.code === 4902) {
        throw new SurfError(
          SurfErrorCode.WRONG_CHAIN,
          `Chain ${chainId} is not added in the wallet`,
          error,
        );
      }

      if (error?.code === 4001) {
        throw new SurfError(
          SurfErrorCode.WALLET_REJECTED,
          "Chain switch rejected by user",
          error,
        );
      }

      throw error;
    }
  }

  async getSigner(): Promise<Signer> {
    return (await this.getBrowserProvider()).getSigner();
  }

  async getProvider(): Promise<Eip1193Provider> {
    return this.requireProvider();
  }

  async getBrowserProvider(): Promise<BrowserProvider> {
    return new BrowserProvider(this.requireProvider());
  }

  async signMessage(message: string): Promise<string> {
    const signer = await this.getSigner();
    return signer.signMessage(message);
  }

  onAccountsChanged(cb: (accounts: string[]) => void): void {
    this.listeners.accountsChanged.push(cb);
  }

  onChainChanged(cb: (chainId: number) => void): void {
    this.listeners.chainChanged.push(cb);
  }

  onDisconnect(cb: () => void): void {
    this.listeners.disconnect.push(cb);
  }

  removeAllListeners(): void {
    const provider = this.provider as
      | (Eip1193Provider & { removeAllListeners?: (event?: string) => void })
      | undefined;
    provider?.removeAllListeners?.("accountsChanged");
    provider?.removeAllListeners?.("chainChanged");
    provider?.removeAllListeners?.("disconnect");
  }

  protected requireProvider(): Eip1193Provider {
    this.provider ??= this.resolveProvider();
    if (!this.provider) {
      throw new SurfError(
        SurfErrorCode.WALLET_NOT_INSTALLED,
        `${this.name} wallet is not installed`,
      );
    }
    return this.provider;
  }

  protected bindListeners(provider: Eip1193Provider): void {
    const evented = provider as Eip1193Provider & {
      on?: (event: string, callback: (...args: any[]) => void) => void;
    };

    evented.on?.("accountsChanged", (accounts: string[]) => {
      this.listeners.accountsChanged.forEach((listener) => listener(accounts));
    });

    evented.on?.("chainChanged", (chainIdHex: string) => {
      const chainId =
        typeof chainIdHex === "string"
          ? Number.parseInt(chainIdHex, 16)
          : Number(chainIdHex);
      this.listeners.chainChanged.forEach((listener) => listener(chainId));
    });

    evented.on?.("disconnect", () => {
      this.listeners.disconnect.forEach((listener) => listener());
    });
  }
}
